import { useEffect, useMemo, useState } from 'react';
import type { AgoraParticipantLike } from '../../types';
import useVideoContext from '../useVideoContext/useVideoContext';

function clampBars(v: number): number {
  // UI expects 0..5-ish where higher = better; we return 0..5.
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(5, Math.round(v)));
}

export default function useParticipantNetworkQualityLevel(participant: AgoraParticipantLike): number | null {
  const { room } = useVideoContext();
  const [qualitiesByUid, setQualitiesByUid] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!room) {
      setQualitiesByUid({});
      return;
    }
    const client = room.client;

    const onNetworkQuality = (...args: any[]) => {
      // Support a few possible signatures:
      // 1) (uid, uplinkQuality, downlinkQuality)
      // 2) ({ uid, uplinkQuality, downlinkQuality })
      const a0 = args?.[0];
      let uid: number | null = null;
      let uplink: number | null = null;
      let downlink: number | null = null;

      if (typeof a0 === 'number' || typeof a0 === 'string') {
        uid = typeof a0 === 'number' ? a0 : parseInt(String(a0), 10);
        uplink = Number(args?.[1]);
        downlink = Number(args?.[2]);
      } else if (a0 && typeof a0 === 'object') {
        uid =
          typeof (a0 as any).uid === 'number'
            ? (a0 as any).uid
            : parseInt(String((a0 as any).uid ?? ''), 10);
        uplink = Number((a0 as any).uplinkQuality ?? (a0 as any).uplinkNetworkQuality);
        downlink = Number((a0 as any).downlinkQuality ?? (a0 as any).downlinkNetworkQuality);
      }

      if (!uid || !Number.isFinite(uid)) return;

      // Agora quality is typically 0..6 where smaller is better in some docs,
      // but in others larger is better. We normalize to "bars higher is better":
      // If value looks like 0..6 where 1=excellent, invert; if 0..5 where higher better, keep.
      const raw = Number.isFinite(downlink as any) ? (downlink as number) : (uplink as number);
      if (!Number.isFinite(raw)) return;

      const normalizedBetterIsHigher =
        raw <= 6 ? 6 - raw : raw; // heuristic; keeps UI usable across SDK variants

      setQualitiesByUid(prev => ({ ...prev, [String(uid)]: normalizedBetterIsHigher }));
    };

    client.on('network-quality', onNetworkQuality as any);
    return () => {
      client.off('network-quality', onNetworkQuality as any);
    };
  }, [room]);

  return useMemo(() => {
    if (!room) return null;
    const v = qualitiesByUid[String(participant.uid)];
    if (v === undefined) return null;
    return clampBars(v);
  }, [room, participant.uid, qualitiesByUid]);
}
